// src/ecrans/couverture.js — LA RÈGLE DES CHIFFRES (consigne du 03/11, une
// seule pour tous les écrans NEST).
//
// Un chiffre d'arrivées, de départs, de ménages, de remplissage ou de nuits
// libres n'est vrai que pour les logements SUIVIS : ceux qu'une source vivante
// de réservations alimente (porte `ecran_journee_lire`, champ `couverture`,
// calculé par lib/ecrans/journee.js). Un logement « sans source » ou à
// « source endormie » n'est pas suivi.
//
//   · tous les logements suivis        → le vrai chiffre, 0 compris ;
//   · une partie seulement             → le chiffre + « sur N logements suivis » ;
//   · aucun suivi, ou on ne sait pas   → « — » (REGLES 72 : « — » = je ne sais pas) ;
//   · pendant la lecture               → « … ».
// « La journée se déroule bien » ne se dit que si `tousSuivis`.
//
// Une seule fonction rend le chiffre (`chiffreSuivi`) ; aucun écran ne
// recompte la couverture de son côté.

/** La couverture « en lecture » : le chiffre vaut « … ». */
export const EN_LECTURE = "lecture";

/**
 * La couverture d'une lecture de `ecran_journee_lire` : EN_LECTURE pendant la
 * lecture, null si la lecture a raté (« je ne sais pas »), sinon l'objet
 * `couverture` de la réponse (null s'il est absent).
 */
export function couvertureDe(lecture) {
  if (!lecture || lecture.etat === "lecture") return EN_LECTURE;
  if (lecture.etat !== "ok") return null;
  return (lecture.data && lecture.data.couverture) || null;
}

/**
 * Combien de logements sont suivis, sur combien. null quand la porte ne le
 * sait pas (repli configuration, réponse sans couverture, lecture ratée).
 */
export function etatSuivi(couverture) {
  if (!couverture || couverture === EN_LECTURE) return null;
  const s = couverture.logements_suivis;
  const t = couverture.logements_total;
  if (!Number.isInteger(s) || !Number.isInteger(t) || s < 0 || t < 0 || s > t) return null;
  return { suivis: s, total: t, tous: t > 0 && s === t, aucun: s === 0 };
}

/** « sur 1 logement suivi », « sur 9 logements suivis ». */
export function phraseSur(suivis) {
  return `sur ${suivis} ${suivis <= 1 ? "logement suivi" : "logements suivis"}`;
}

/**
 * LE chiffre affiché. `n` : le nombre compté (ou « … », « — », null, NaN).
 * Rend { valeur, sur, tousSuivis } : `valeur` est un nombre, « — » ou « … » ;
 * `sur` vaut null ou « sur N logements suivis ».
 */
export function chiffreSuivi(n, couverture) {
  if (n === "…" || couverture === EN_LECTURE) return { valeur: "…", sur: null, tousSuivis: false };
  const e = etatSuivi(couverture);
  if (typeof n !== "number" || !Number.isFinite(n) || !e || e.aucun)
    return { valeur: "—", sur: null, tousSuivis: false };
  if (e.tous) return { valeur: n, sur: null, tousSuivis: true };
  return { valeur: n, sur: phraseSur(e.suivis), tousSuivis: false };
}

/**
 * Les logements NON suivis, nommés (pour les écarter d'un calcul ou les dire) :
 * `logements` = sans source + source endormie ; `inconnu` = la porte ne sait
 * pas (repli configuration) ; `absente` = la réponse ne dit rien.
 */
export function nonCouverts(journee) {
  const c = journee && journee.couverture;
  if (!c) return { logements: [], inconnu: false, depuis: null, absente: true };
  const sans = Array.isArray(c.logements_sans_source) ? c.logements_sans_source : [];
  const figes = Array.isArray(c.logements_source_figee) ? c.logements_source_figee : [];
  const vus = new Set();
  const logements = [...sans.map((l) => ({ ...l, motif: "sans source" })), ...figes.map((l) => ({ ...l, motif: "source endormie" }))]
    .filter((l) => (vus.has(l.id) ? false : vus.add(l.id)));
  return {
    logements,
    inconnu: c.logements_sans_source === null || c.logements_source_figee === null,
    depuis: figes.length ? c.reservations_figees_depuis || null : null,
  };
}

/**
 * Les identifiants des logements NON suivis (sans source + source endormie),
 * pour les écarter AVANT de compter. LA seule union des deux listes : le
 * Calendrier, Opérations et l'Accueil passent par ici.
 */
export function idsNonSuivis(couverture) {
  if (!couverture || couverture === EN_LECTURE) return new Set();
  return new Set(nonCouverts({ couverture }).logements.map((l) => l.id));
}

/** Un élément (séjour, ménage) est-il sur un logement suivi ? */
const surSuivi = (exclus) => (x) => !(x && x.logement && exclus.has(x.logement.id));

/**
 * Le jour de `ecran_journee_lire` restreint aux logements SUIVIS : arrivées,
 * départs et ménages des logements non suivis sont retirés avant de compter
 * (un séjour figé d'une source endormie n'entre pas dans « 0 arrivée sur N
 * logements suivis »). null reste null.
 */
export function jourSuivi(jour, couverture) {
  if (!jour) return jour;
  const garde = surSuivi(idsNonSuivis(couverture));
  return {
    ...jour,
    arrivees: (jour.arrivees || []).filter(garde),
    departs: (jour.departs || []).filter(garde),
    menages: (jour.menages || []).filter(garde),
  };
}

/** La phrase « — » qui nomme les logements non suivis (ou null s'il n'y en a pas). */
export function phraseNonCouverts(nc) {
  if (!nc) return null;
  if (nc.inconnu) return "— Couverture des sources inconnue (liste lue dans la configuration) : un 0 peut cacher un séjour.";
  if (!nc.logements.length) return null;
  const noms = (liste) => liste.map((l) => l.nom || l.id).join(", ");
  const sans = nc.logements.filter((l) => l.motif === "sans source");
  const figes = nc.logements.filter((l) => l.motif !== "sans source");
  const morceaux = [];
  // Sans source : aucune réservation n'entre, rien n'est compté.
  if (sans.length)
    morceaux.push(`— ${noms(sans)} : aucune source de réservations, leurs arrivées et départs ne sont pas comptés.`);
  // Source endormie : les réservations déjà là SONT comptées, figées.
  if (figes.length) {
    const date = nc.depuis
      ? new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(nc.depuis))
      : null;
    morceaux.push(
      `— ${noms(figes)} : source endormie, réservations comptées telles qu'${date ? `au ${date}` : "à leur dernière mise à jour (date inconnue)"} ; les nouvelles n'apparaissent pas.`,
    );
  }
  return morceaux.join(" ");
}
